import React, { memo, useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import { Confirm, Datepicker, Input, InputType, Label, Modal, RadioButton } from '@components/common';
import SubmissionButtons from '@components/common/SubmissionButtons';

import { useMutateCommissionSettingsRecalculate } from '@hooks/organisation/useMutateCommissionSettingsRecalculate';

import { isEmpty } from '@utils/common';
import { formatDate } from '@utils/date';
import { removeSpecialCharacters } from '@utils/removeSpecialCharacters';

import { EValidationMode } from '@enums/validationMode';

import { MODAL_WIDTH_480 } from '@constants/modals';
import { regExpDecimal, regExpDecimalOnlyTenths, regExpNumbers } from '@constants/regExp';
import { ECommissionDate, ECommissionType, TCommissionDate, TCommissionType } from '../constants';
import { ICommissionFormFields } from './constants';

import { schema } from './schema';

import styles from './CommissionSettingsForm.module.scss';

interface ICommissionSettingsForm {
	onClose: () => void;
	organisationId?: string;
	setIsDirtyForm: (value: boolean) => void;
	closeCommissionForm: () => void;
}

const CommissionSettingsForm = ({
	onClose,
	organisationId,
	setIsDirtyForm,
	closeCommissionForm,
}: ICommissionSettingsForm) => {
	const [isConfirmModal, setIsConfirmModal] = useState(false);

	const {
		handleRecalculateCommissionSettings,
		isLoadingRecalculateCommissionSettings,
	} = useMutateCommissionSettingsRecalculate();

	const {
		register,
		handleSubmit,
		formState: { errors, isDirty },
		setValue,
		watch,
		clearErrors,
		trigger,
	} = useForm<ICommissionFormFields>({
		mode: EValidationMode.onChange,
		resolver: yupResolver(schema),
		defaultValues: {
			commissionType: ECommissionType.noCommission,
			percentValue: null,
			fixedValue: null,
			date: null,
			dateType: ECommissionDate.today,
		},
	});

	const commissionType = watch('commissionType');
	const dateType = watch('dateType');
	const date = watch('date');

	useEffect(() => {
		clearErrors();
	}, [commissionType, dateType, clearErrors]);

	useEffect(() => {
		setIsDirtyForm(isDirty);
	}, [isDirty, setIsDirtyForm]);

	const onChangeCommissionType = useCallback((value: TCommissionType) => setValue('commissionType', value), [setValue]);
	const onChangeDateType = useCallback((value: TCommissionDate) => setValue('dateType', value), [setValue]);

	const setDate = useCallback(async (value: number | null) => {
		await setValue('date', value);
		await trigger('date');
	}, [setValue, trigger]);

	const openConfirmModal = useCallback(() => setIsConfirmModal(true), []);
	const closeConfirmModal = useCallback(() => setIsConfirmModal(false), []);

	const onSubmit = useCallback((values: ICommissionFormFields) => {
		const from = date ? formatDate(date) : formatDate(Date.now());

		const value = values.commissionType === ECommissionType.fixed
			? values?.fixedValue
			: values?.percentValue;

		handleRecalculateCommissionSettings({
			id: organisationId,
			fromText: from,
			commissionType: values.commissionType !== ECommissionType.noCommission
				? values.commissionType
				: ECommissionType.fixed,
			value: value || 0,
		});

		closeConfirmModal();
		closeCommissionForm();
	}, [
		handleRecalculateCommissionSettings,
		closeCommissionForm,
		closeConfirmModal,
		organisationId,
		date,
	]);

	const onPercentValueChange = useCallback((value: string) => {
		const formattedValue = !regExpDecimalOnlyTenths.test(value) ? value.slice(0, -1) : value;

		removeSpecialCharacters(formattedValue, 'percentValue', setValue, regExpDecimal);
	}, [setValue]);

	return (
		<>
			<Modal
				isOpen={isConfirmModal}
				onClose={closeConfirmModal}
				title='Confirm action'
				width={MODAL_WIDTH_480}
			>
				<Confirm
					onClose={closeConfirmModal}
					onSubmit={handleSubmit(onSubmit)}
					question='Are you sure you want to apply changes?'
				>
					All totals from the chosen date will be recalculated if the given date is in the past.
				</Confirm>
			</Modal>
			<div className={styles.radioButtonsContainer}>
				<Label
					className={styles.labelText}
					label='Type'
					required
				/>
				<div className={styles.radioButtonContainer}>
					<RadioButton
						checked={commissionType === ECommissionType.noCommission}
						id={ECommissionType.noCommission}
						label='No commission'
						name='commissionType'
						onChange={onChangeCommissionType}
						register={register}
						value={ECommissionType.noCommission}
					/>
				</div>
				<div className={styles.radioButtonContainer}>
					<RadioButton
						checked={commissionType === ECommissionType.percent}
						id={ECommissionType.percent}
						label='% of arranged assets'
						name='commissionType'
						onChange={onChangeCommissionType}
						register={register}
						value={ECommissionType.percent}
					/>
					<div className={styles.percentInput}>
						<Input
							disabled={commissionType !== ECommissionType.percent}
							name='percentValue'
							onChange={onPercentValueChange}
							placeholder='%'
							register={register}
							type={InputType.float}
						/>
					</div>
				</div>
				{!!errors?.percentValue
					&& <div className={styles.errorMessageText}>
						{errors.percentValue?.message}
					</div>
				}
				<div className={styles.radioButtonContainer}>
					<RadioButton
						checked={commissionType === ECommissionType.fixed}
						id={ECommissionType.fixed}
						label='Fixed rate per an arranged asset'
						name='commissionType'
						onChange={onChangeCommissionType}
						register={register}
						value={ECommissionType.fixed}
					/>
					<div className={styles.fixedValueInput}>
						<Input
							disabled={commissionType !== ECommissionType.fixed}
							name='fixedValue'
							placeholder='Value'
							regexValidation={regExpNumbers}
							register={register}
							setValue={setValue}
							type={InputType.price}
						/>
					</div>
				</div>
				{!!errors?.fixedValue
					&& <div className={styles.errorMessageText}>
						{errors.fixedValue?.message}
					</div>
				}
			</div>
			<div className={styles.radioButtonsContainer}>
				<Label
					className={styles.labelText}
					label='Start from'
					required
				/>
				<div className={styles.radioButtonContainer}>
					<RadioButton
						checked={dateType === ECommissionDate.today}
						id={ECommissionDate.today}
						label='Today'
						name='dateType'
						onChange={onChangeDateType}
						register={register}
						value={ECommissionDate.today}
					/>
				</div>
				<div className={styles.radioButtonContainer}>
					<RadioButton
						checked={dateType === ECommissionDate.startFromDate}
						id={ECommissionDate.startFromDate}
						label='Selected date'
						name='dateType'
						onChange={onChangeDateType}
						register={register}
						value={ECommissionDate.startFromDate}
					/>
					<div className={styles.datepickerContainer}>
						<Datepicker
							disableFutureDates
							disableInput
							disabled={dateType !== ECommissionDate.startFromDate}
							handleChange={setDate}
							isClearable
							isMobileDevice={false}
							isTodayButton={false}
							required
							selected={date}
						/>
					</div>
				</div>
				{!!errors?.date
					&& <div className={styles.errorMessageText}>
						{errors?.date?.message?.toString()}
					</div>
				}
			</div>
			<SubmissionButtons
				className={styles.submissionButtons}
				disabledPositive={!isEmpty(errors)}
				isLoading={isLoadingRecalculateCommissionSettings}
				negativeHandler={onClose}
				positiveHandler={handleSubmit(openConfirmModal)}
				positiveLabel='Save'
			/>
		</>
	);
};

export default memo(CommissionSettingsForm);
